import { BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery, retry } from '@reduxjs/toolkit/query';
import Cookie from 'js-cookie';

import { showDefaultErrorAlert } from '../alert/actions';

import { session } from 'core/utils/AppSession';
import { MixpanelAgent } from 'core/utils';

const DRF_AUTH_FAILURE_DETAIL = 'Authentication credentials were not provided.';
const DRF_AUTH_FAILURE_CODE = 'not_authenticated';

/**
 * The API answers unauthenticated requests in the drf-standardized-errors shape,
 * `{type, errors: [{code, detail, attr}]}`, so a check against a top-level `detail`
 * never matches and the session is left to rot until the user reloads. The flat
 * shape is still accepted for any endpoint that has not been standardised.
 */
export function isAuthenticationError(error: FetchBaseQueryError): boolean {
  if (error.status !== 403) return false;

  const data = error.data as { detail?: string; errors?: { code?: string }[] } | null;

  if (data?.detail === DRF_AUTH_FAILURE_DETAIL) return true;

  return data?.errors?.some((entry) => entry?.code === DRF_AUTH_FAILURE_CODE) ?? false;
}

const baseQuery = retry(
  fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers) => {
      headers.set('X-CSRFToken', Cookie.get('csrftoken') ?? '');
      return headers;
    },
  }),
  { maxRetries: 1 },
);

// A custom query wrapper that handles auth failures and dispatches
// a global error action when the extraOptions.triggerGlobalError flag is set.
const customQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  { triggerGlobalError?: boolean; maxRetries?: number }
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && isAuthenticationError(result.error)) {
    MixpanelAgent.track('auto-signout');
    session.endSession();
    window.location.href = window.location.origin;
    return result;
  }

  if (result.error && extraOptions?.triggerGlobalError) {
    api.dispatch(showDefaultErrorAlert());
  }

  return result;
};

export default customQuery;
